/**
 * Small RFC 4180 CSV parser: quoted fields, escaped quotes, commas and line
 * breaks inside quotes, CRLF or LF, a leading byte-order mark, and comma,
 * semicolon or tab delimiters (detected from the header line).
 */
export interface ParsedTable {
  headers: string[];
  rows: Record<string, string>[];
  delimiter: string;
}

export function detectDelimiter(text: string): string {
  let firstLine = "";
  let inQuotes = false;
  for (const ch of text) {
    if (ch === '"') inQuotes = !inQuotes;
    else if ((ch === "\n" || ch === "\r") && !inQuotes) break;
    firstLine += ch;
  }
  const counts = [",", ";", "\t"].map((d) => [d, firstLine.split(d).length - 1] as const);
  counts.sort((a, b) => b[1] - a[1]);
  return counts[0]![1] > 0 ? counts[0]![0] : ",";
}

export function parseCsvRecords(text: string, delimiter = ","): string[][] {
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const records: string[][] = [];
  let field = "";
  let record: string[] = [];
  let inQuotes = false;
  let i = 0;
  while (i < src.length) {
    const ch = src[i]!;
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      field += ch;
      i++;
      continue;
    }
    if (ch === '"' && field === "") {
      inQuotes = true;
      i++;
    } else if (ch === delimiter) {
      record.push(field);
      field = "";
      i++;
    } else if (ch === "\r" || ch === "\n") {
      record.push(field);
      records.push(record);
      record = [];
      field = "";
      i += ch === "\r" && src[i + 1] === "\n" ? 2 : 1;
    } else {
      field += ch;
      i++;
    }
  }
  if (field !== "" || record.length > 0) {
    record.push(field);
    records.push(record);
  }
  // Drop fully blank lines
  return records.filter((r) => r.some((f) => f.trim() !== ""));
}

/** Parse CSV text into header-keyed rows. Duplicate or blank headers get a numbered name. */
export function parseCsv(text: string): ParsedTable {
  const delimiter = detectDelimiter(text);
  const records = parseCsvRecords(text, delimiter);
  const head = records[0] ?? [];
  const seen = new Map<string, number>();
  const headers = head.map((h, idx) => {
    const base = h.trim() || `Column ${idx + 1}`;
    const n = (seen.get(base) ?? 0) + 1;
    seen.set(base, n);
    return n === 1 ? base : `${base} (${n})`;
  });
  const rows = records.slice(1).map((r) => {
    const obj: Record<string, string> = {};
    headers.forEach((h, k) => (obj[h] = r[k] ?? ""));
    return obj;
  });
  return { headers, rows, delimiter };
}
